import { isAuthorized } from 'services';
import { DashboardCardConfig, CardId } from '../types/DashboardCardConfig';
import {
    DashboardTabConfig,
    DashboardTabState,
    TabId,
    TabColorKey,
    DEFAULT_TAB_COLOR_KEY,
    isTabColorKey,
    DEFAULT_TAB_ID,
    DEFAULT_TAB_LABEL,
    MAX_TABS,
    MAX_CARDS_PER_TAB,
} from '../types/DashboardTabConfig';
import { DashboardTabDto, LkDashboardCard, LkReactMenu } from 'generated/openApi';
import ApiService from 'services/apiService';
import { CARD_CATEGORY_MAP, CARD_PERMISSION_MAP, CARD_TENANT_MAP } from '../config/cardConfigs';

export class DashboardConfigService {

    private static cardDefinitions: LkDashboardCard[] = [];

    static setupDashboardTabs(serverTabs: DashboardTabDto[], cardDefinitions: LkDashboardCard[]): DashboardTabState {
        this.cardDefinitions = cardDefinitions;

        if (!serverTabs || serverTabs.length === 0) {
            return this.createFreshState();
        }
        const tabs: DashboardTabConfig[] = [...serverTabs]
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map((serverTab) => this.buildTabConfig(serverTab));
        const defaultTab = tabs.find((t) => t.isDefault);
        return {
            tabs,
            activeTabId: defaultTab?.id ?? tabs[0]?.id ?? DEFAULT_TAB_ID,
            lastUpdated: new Date().toISOString(),
        };
    }

    private static mapCardDef(def: LkDashboardCard, overrides: Partial<DashboardCardConfig>): DashboardCardConfig {
        return {
            id: def.id as CardId,
            name: def.name,
            description: def.description,
            icon: def.icon,
            category: CARD_CATEGORY_MAP[def.id] ?? def.category,
            isNew: def.isNew,
            requiredPermissions: CARD_PERMISSION_MAP[def.id],
            enabled: false,
            displayOrder: def.displayOrder ?? 0,
            ...overrides,
        };
    }

    private static buildTabConfig(serverTab: DashboardTabDto): DashboardTabConfig {
        const serverCards = serverTab.cards ?? [];
        const serverCardMap = new Map<number, number>(
            serverCards.map((sc) => [sc.cardId, sc.displayOrder ?? 0])
        );
        const cards: DashboardCardConfig[] = this.cardDefinitions.map((def) => {
            const serverOrder = serverCardMap.get(def.id);
            const isOnTab = serverOrder !== undefined;
            return this.mapCardDef(def, {
                enabled: isOnTab,
                displayOrder: isOnTab ? serverOrder : 999 + (def.displayOrder ?? 0),
            });
        }).sort((a, b) => a.displayOrder - b.displayOrder);
        return {
            id: String(serverTab.id),
            label: serverTab.label ?? DEFAULT_TAB_LABEL,
            isDefault: serverTab.isDefault,
            colorKey: isTabColorKey(serverTab.color) ? serverTab.color : DEFAULT_TAB_COLOR_KEY,
            cards,
        };
    }

    private static createFreshState(): DashboardTabState {
        const cards = this.cardDefinitions.map((def) => this.mapCardDef(def, { enabled: false }));
        return {
            tabs: [{
                id: DEFAULT_TAB_ID,
                label: DEFAULT_TAB_LABEL,
                isDefault: true,
                colorKey: DEFAULT_TAB_COLOR_KEY,
                cards,
            }],
            activeTabId: DEFAULT_TAB_ID,
            lastUpdated: new Date().toISOString(),
        };
    }

    private static buildEmptyCardSet(): DashboardCardConfig[] {
        return this.cardDefinitions.map((def) => this.mapCardDef(def, { enabled: false }));
    }

    // ─── Tab Operations

    static async createTab(
        state: DashboardTabState,
        label: string
    ): Promise<DashboardTabState> {
        if (state.tabs.length >= MAX_TABS) {
            console.warn(`Cannot exceed ${MAX_TABS} tabs.`);
            return state;
        }
        if (!label.trim()) {
            console.warn('Tab label cannot be empty.');
            return state;
        }

        const newTabId = await ApiService.dashboardTab.saveCreate({
            id: 0,
            label: label.trim(),
            displayOrder: state.tabs.length,
            cards: [],
        });

        const newTab: DashboardTabConfig = {
            id: String(newTabId),
            label: label.trim(),
            isDefault: false,
            colorKey: DEFAULT_TAB_COLOR_KEY,
            cards: this.buildEmptyCardSet(),
        };

        return {
            ...state,
            tabs: [...state.tabs, newTab],
            activeTabId: newTab.id,
            lastUpdated: new Date().toISOString(),
        };
    }

    static async deleteTab(
        state: DashboardTabState,
        tabId: TabId
    ): Promise<DashboardTabState> {
        const tabToDelete = state.tabs.find((t) => t.id === tabId);
        if (!tabToDelete || tabToDelete.isDefault) {
            console.warn('Cannot delete the default tab.');
            return state;
        }

        await ApiService.dashboardTab.delete(Number(tabId));

        const updatedTabs = state.tabs.filter((t) => t.id !== tabId);
        const newActiveTabId =
            state.activeTabId === tabId ? (updatedTabs.find(t => t.isDefault)?.id ?? updatedTabs[0]?.id ?? DEFAULT_TAB_ID) : state.activeTabId;

        return {
            ...state,
            tabs: updatedTabs,
            activeTabId: newActiveTabId,
            lastUpdated: new Date().toISOString(),
        };
    }

    static renameTab(
        state: DashboardTabState,
        tabId: TabId,
        newLabel: string
    ): DashboardTabState {
        if (!newLabel.trim()) {
            console.warn('Tab label cannot be empty.');
            return state;
        }

        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab || tab.isDefault) return state;

        const updated: DashboardTabState = {
            ...state,
            tabs: state.tabs.map((t) =>
                t.id === tabId ? { ...t, label: newLabel.trim() } : t
            ),
            lastUpdated: new Date().toISOString(),
        };

        this.persistTabUpdate(tab, { label: newLabel.trim() });

        return updated;
    }

    static setTabColor(
        state: DashboardTabState,
        tabId: TabId,
        colorKey: TabColorKey
    ): DashboardTabState {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return state;

        const updated: DashboardTabState = {
            ...state,
            tabs: state.tabs.map((t) =>
                t.id === tabId ? { ...t, colorKey } : t
            ),
            lastUpdated: new Date().toISOString(),
        };

        this.persistTabUpdate(tab, { colorKey });

        return updated;
    }

    static setActiveTab(
        state: DashboardTabState,
        tabId: TabId
    ): DashboardTabState {
        if (!state.tabs.some((t) => t.id === tabId)) {
            console.warn(`Tab ${tabId} not found.`);
            return state;
        }
        return { ...state, activeTabId: tabId };
    }

    // ─── Card Operations

    static toggleCard(
        state: DashboardTabState,
        tabId: TabId,
        cardId: CardId
    ): DashboardTabState {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return state;

        const enabledCount = tab.cards.filter((c) => c.enabled).length;
        const targetCard = tab.cards.find((c) => c.id === cardId);

        if (targetCard && !targetCard.enabled && enabledCount >= MAX_CARDS_PER_TAB) {
            console.warn(`Cannot exceed ${MAX_CARDS_PER_TAB} cards per tab.`);
            return state;
        }

        const updatedCards = tab.cards.map((card) =>
            card.id === cardId
                ? { ...card, enabled: !card.enabled }
                : card
        );

        const updated: DashboardTabState = {
            ...state,
            tabs: state.tabs.map((t) =>
                t.id === tabId ? { ...t, cards: updatedCards } : t
            ),
            lastUpdated: new Date().toISOString(),
        };

        this.persistTabCards(tab, updatedCards);

        return updated;
    }

    static reorderCards(
        state: DashboardTabState,
        tabId: TabId,
        reorderedCards: DashboardCardConfig[]
    ): DashboardTabState {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return state;

        const updated: DashboardTabState = {
            ...state,
            tabs: state.tabs.map((t) =>
                t.id === tabId ? { ...t, cards: reorderedCards } : t
            ),
            lastUpdated: new Date().toISOString(),
        };

        this.persistTabCards(tab, reorderedCards);

        return updated;
    }

    // ─── API Persistence Helpers

    private static persistTabUpdate(
        tab: DashboardTabConfig,
        changes: { label?: string; colorKey?: TabColorKey }
    ): void {
        const enabledCards = tab.cards.filter((c) => c.enabled);

        ApiService.dashboardTab.saveUpdate({
            id: Number(tab.id),
            label: changes.label ?? tab.label,
            color: changes.colorKey ?? tab.colorKey,
            displayOrder: undefined,
            cards: enabledCards.map((card, index) => ({
                cardId: card.id,
                displayOrder: index,
            })),
        }).catch((error) => {
            console.error('Failed to persist tab update:', error);
        });
    }

    private static persistTabCards(
        tab: DashboardTabConfig,
        cards: DashboardCardConfig[]
    ): void {
        const enabledCards = cards.filter((c) => c.enabled);

        ApiService.dashboardTab.saveUpdate({
            id: Number(tab.id),
            label: tab.label,
            color: tab.colorKey,
            displayOrder: undefined,
            cards: enabledCards.map((card, index) => ({
                cardId: card.id,
                displayOrder: index,
            })),
        }).catch((error) => {
            console.error('Failed to persist tab cards:', error);
        });
    }

    // ─── Authorization & Visibility

    private static hasRequiredPermissions(card: DashboardCardConfig): boolean {
        if (!card.requiredPermissions || card.requiredPermissions.length === 0) {
            return true;
        }
        return card.requiredPermissions.every((req) =>
            isAuthorized(req.permission, req.action)
        );
    }

   private static hasRequiredMenuAccess(card: DashboardCardConfig, reactMenus?: LkReactMenu[]): boolean {
        const requiredMenuId = CARD_TENANT_MAP[card.id];
        if (requiredMenuId === undefined) return true;
        if (!reactMenus) return false;
        return reactMenus.some((m) => m.id === requiredMenuId); 
    }

    static filterByAuthorization(
        cards: DashboardCardConfig[],
        reactMenus?: LkReactMenu[]
    ): DashboardCardConfig[] {
        return cards.filter((card) =>
            this.hasRequiredPermissions(card) && this.hasRequiredMenuAccess(card, reactMenus)
        );
    }

    static getVisibleCards(
        state: DashboardTabState,
        tabId: TabId,
        reactMenus?: LkReactMenu[]
    ): DashboardCardConfig[] {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return [];

        return this.filterByAuthorization(tab.cards, reactMenus)
            .filter((card) => card.enabled)
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }

    static getAllCardsForTab(
        state: DashboardTabState,
        tabId: TabId,
        reactMenus?: LkReactMenu[]
    ): DashboardCardConfig[] {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return [];
        return this.filterByAuthorization(tab.cards, reactMenus);
    }

    static isTabAtCardLimit(state: DashboardTabState, tabId: TabId): boolean {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) return false;
        return tab.cards.filter((c) => c.enabled).length >= MAX_CARDS_PER_TAB;
    }

    static getActiveTab(
        state: DashboardTabState
    ): DashboardTabConfig | undefined {
        return state.tabs.find((t) => t.id === state.activeTabId);
    }
}
